import type { PartnerSchemaMappings } from '@reach/shared-types/schema-mappings';
import {
  SCHEMA_LOOKUP_DEFAULT_LIMIT,
  SCHEMA_LOOKUP_MAX_FILTERS,
  SCHEMA_LOOKUP_MAX_LIMIT,
} from '@reach/shared-types/tool-lookup';
import {
  HTTP_TOOL_FIELDS,
  type FieldToExpose,
  type SchemaDefinition,
  type SchemaLookupToolDefinitionInput,
  type ToolDefinition,
  type ToolDefinitionInput,
  type ToolLookupConfigInput,
  type ToolLookupFilter,
} from '../../lib/api';
import { BLANK_TOOL_DRAFT } from './tool-inputs';

export {
  SCHEMA_LOOKUP_DEFAULT_LIMIT as LOOKUP_DEFAULT_LIMIT,
  SCHEMA_LOOKUP_MAX_FILTERS as LOOKUP_MAX_FILTERS,
  SCHEMA_LOOKUP_MAX_LIMIT as LOOKUP_MAX_LIMIT,
};

export const LOOKUP_ANNOTATIONS = {
  readOnlyHint: true,
  destructiveHint: false,
  idempotentHint: true,
  openWorldHint: false,
} as const;

export type LookupScalarType = 'string' | 'integer' | 'number' | 'boolean';

export interface LookupSchemaField {
  path: string;
  title: string;
  description?: string;
  type: LookupScalarType;
  enumValues?: Array<string | number | boolean>;
}

export interface LookupSchemaSetup {
  id: string;
  name: string;
  schema: SchemaDefinition | null;
  recordIdField?: string;
  userIdField?: string;
  dateField?: string;
  returnedFields: FieldToExpose[];
  filterFields: LookupSchemaField[];
  reasons: string[];
  needsMappingSetup: boolean;
  needsSchemaSetup: boolean;
  isEligible: boolean;
}

interface CommonDraftFields {
  display_name: string;
  description: string;
  required_scope: string;
  status: 'active' | 'inactive';
}

const commonFields = (draft: ToolDefinitionInput): CommonDraftFields => ({
  display_name: draft.display_name,
  description: draft.description,
  required_scope: draft.required_scope,
  status: draft.status,
});

/** Creates a schema-lookup draft while preserving fields shared with an unsaved HTTP draft. */
export function createLookupDraft(source?: ToolDefinitionInput): SchemaLookupToolDefinitionInput {
  const common = source
    ? commonFields(source)
    : {
        display_name: '',
        description: '',
        required_scope: 'partner_user',
        status: 'active' as const,
      };

  return {
    ...common,
    execution_kind: 'schema_lookup',
    lookup_config: {
      schema_definition_id: '',
      filters: [],
      limit: SCHEMA_LOOKUP_DEFAULT_LIMIT,
    },
    annotations: LOOKUP_ANNOTATIONS,
  };
}

/** Creates an HTTP draft while preserving fields shared with an unsaved lookup draft. */
export function createHttpDraft(source?: ToolDefinitionInput): typeof BLANK_TOOL_DRAFT {
  if (!source) return BLANK_TOOL_DRAFT;
  return { ...BLANK_TOOL_DRAFT, ...commonFields(source) };
}

const savedLookupInput = (config: ToolLookupConfigInput) => ({
  schema_definition_id: config.schema_definition_id,
  filters: config.filters,
  limit: config.limit,
});

/** Converts a stored tool definition into its editable discriminated-union variant. */
export function toolDefinitionToInput(tool: ToolDefinition): ToolDefinitionInput {
  if (tool.execution_kind === 'schema_lookup') {
    return {
      display_name: tool.display_name,
      description: tool.description,
      execution_kind: 'schema_lookup',
      lookup_config: savedLookupInput(tool.lookup_config),
      required_scope: tool.required_scope,
      annotations: tool.annotations,
      status: tool.status,
    };
  }

  return {
    display_name: tool.display_name,
    description: tool.description,
    execution_kind: 'http',
    http_method: tool.http_method,
    base_url: tool.base_url,
    path_template: tool.path_template,
    auth_type: tool.auth_type,
    auth_config: tool.auth_config,
    credentials: tool.credentials,
    request_schema: tool.request_schema,
    request_mapping: tool.request_mapping,
    response_mapping: tool.response_mapping,
    schema_mapping_refs: tool.schema_mapping_refs,
    required_scope: tool.required_scope,
    cacheable: tool.cacheable,
    cache_ttl_ms: tool.cache_ttl_ms,
    claim_enforcement: tool.claim_enforcement,
    timeout_ms: tool.timeout_ms,
    retry_policy: tool.retry_policy,
    annotations: tool.annotations,
    variables: tool.variables,
    status: tool.status,
  };
}

/**
 * Reads the exposure list off a schema definition. `SchemaDefinition` declares both the
 * camel- and snake-cased spelling, so both have to be tolerated until that type is unified.
 */
export const schemaFieldsToExpose = (schema: SchemaDefinition): FieldToExpose[] =>
  schema.fieldsToExpose ?? schema.fields_to_expose ?? [];

const scalarType = (property: Record<string, unknown>): LookupScalarType | null => {
  const type = property.type;
  return type === 'string' || type === 'integer' || type === 'number' || type === 'boolean'
    ? type
    : null;
};

const scalarFieldsFromProperties = (
  properties: Record<string, unknown>,
  prefix = '',
): LookupSchemaField[] => {
  const fields: LookupSchemaField[] = [];

  Object.entries(properties).forEach(([name, value]) => {
    if (!value || typeof value !== 'object') return;
    const property = value as Record<string, unknown>;
    const path = prefix ? `${prefix}.${name}` : name;
    if (property.properties && typeof property.properties === 'object') {
      fields.push(...scalarFieldsFromProperties(property.properties as Record<string, unknown>, path));
      return;
    }

    const type = scalarType(property);
    if (!type) return;
    const enumValues = Array.isArray(property.enum)
      ? property.enum.filter(
          (entry): entry is string | number | boolean =>
            typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean',
        )
      : undefined;
    fields.push({
      path,
      title: typeof property.title === 'string' ? property.title : name,
      description: typeof property.description === 'string' ? property.description : undefined,
      type,
      enumValues,
    });
  });

  return fields;
};

/** Extracts filterable scalar paths and their JSON Schema control metadata. */
export function lookupScalarFields(schema: Record<string, unknown>): LookupSchemaField[] {
  const properties = schema.properties;
  return properties && typeof properties === 'object'
    ? scalarFieldsFromProperties(properties as Record<string, unknown>)
    : [];
}

/** Picks the initial fixed-filter value when a field is first selected. */
export function defaultLookupFilterValue(field: LookupSchemaField): ToolLookupFilter['value'] {
  if (field.enumValues?.length) return field.enumValues[0] ?? '';
  if (field.type === 'boolean') return false;
  if (field.type === 'integer' || field.type === 'number') return 0;
  return '';
}

/** Checks a stored fixed-filter value against the field's current scalar type and enum. */
export function lookupFilterValueMatchesField(
  field: LookupSchemaField,
  value: string | number | boolean,
): boolean {
  const typeMatches =
    (field.type === 'string' && typeof value === 'string') ||
    (field.type === 'boolean' && typeof value === 'boolean') ||
    (field.type === 'integer' && typeof value === 'number' && Number.isInteger(value)) ||
    (field.type === 'number' && typeof value === 'number' && Number.isFinite(value));
  if (!typeMatches) return false;

  return !field.enumValues?.length || field.enumValues.some(option => Object.is(option, value));
}

/** Builds eligible and needs-setup choices from schema definitions and custom-schema mappings. */
export function buildLookupSchemaSet(args: {
  schemas: SchemaDefinition[];
  mappings: PartnerSchemaMappings | undefined;
  storedConfig?: ToolLookupConfigInput;
}): LookupSchemaSetup[] {
  const customMappings = args.mappings?.customSchema ?? [];
  const mappingBySchemaId = new Map(customMappings.map(mapping => [mapping.schemaId, mapping]));
  const mappedSchemaIds = new Set(customMappings.map(mapping => mapping.schemaId));
  const schemasById = new Map(args.schemas.map(schema => [schema.id, schema]));
  const candidateIds = new Set<string>();

  args.schemas.forEach(schema => {
    if (schema.category === 'custom_schema') candidateIds.add(schema.id);
  });
  mappedSchemaIds.forEach(schemaId => candidateIds.add(schemaId));
  if (args.storedConfig) candidateIds.add(args.storedConfig.schema_definition_id);

  return Array.from(candidateIds)
    .map(id => {
      const schema = schemasById.get(id) ?? null;
      const mapping = mappingBySchemaId.get(id);
      const returnedFields = schema
        ? schemaFieldsToExpose(schema).filter(field => field.exposure.includes('agent-tools'))
        : [];
      const filterFields = schema ? lookupScalarFields(schema.schema) : [];
      const schemaProperties =
        schema?.schema.properties && typeof schema.schema.properties === 'object'
          ? (schema.schema.properties as Record<string, unknown>)
          : {};
      const reasons: string[] = [];
      let needsSchemaSetup = false;

      if (!schema) {
        reasons.push('The mapped schema definition no longer exists.');
        needsSchemaSetup = true;
      } else {
        if (schema.category !== 'custom_schema') {
          reasons.push('Schema lookups require a custom schema.');
          needsSchemaSetup = true;
        }
        if (schema.deactivatedAt || schema.deactivated_at) {
          reasons.push('The schema is inactive.');
          needsSchemaSetup = true;
        }
      }
      const needsMappingSetup =
        !mapping ||
        !mapping.id_field?.trim() ||
        !mapping.userId?.trim() ||
        Boolean(
          schema &&
            mapping &&
            [mapping.id_field, mapping.userId, mapping.date].some(
              fieldName =>
                fieldName?.trim() && !Object.prototype.hasOwnProperty.call(schemaProperties, fieldName),
            ),
        );
      if (needsMappingSetup) {
        reasons.push('Map the record ID and customer ID for this schema.');
      }
      if (schema && returnedFields.length === 0) {
        reasons.push('Select at least one field for Agentic Tools in Data Integration.');
        needsSchemaSetup = true;
      }

      return {
        id,
        name: schema?.name ?? `Unavailable schema (${id})`,
        schema,
        recordIdField: mapping?.id_field,
        userIdField: mapping?.userId,
        dateField: mapping?.date,
        returnedFields,
        filterFields,
        reasons,
        needsMappingSetup,
        needsSchemaSetup,
        isEligible: reasons.length === 0,
      };
    })
    .sort((left, right) => left.name.localeCompare(right.name));
}

const valuesAreEqual = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

/** Builds the minimal PATCH for a saved tool, including stale lookup repairs when requested. */
export function buildToolUpdatePatch({
  saved,
  draft,
  forceLookupConfig,
}: {
  saved: ToolDefinition;
  draft: ToolDefinitionInput;
  forceLookupConfig?: boolean;
}): Partial<ToolDefinitionInput> {
  if (saved.execution_kind !== draft.execution_kind) return {};

  const patch: Record<string, unknown> = {};
  if (draft.description !== saved.description) patch.description = draft.description;
  if (draft.required_scope !== saved.required_scope) patch.required_scope = draft.required_scope;
  if (!valuesAreEqual(draft.annotations, saved.annotations)) patch.annotations = draft.annotations;
  if (draft.status !== saved.status) patch.status = draft.status;

  const patchIsDeactivation =
    draft.status === 'inactive' && Object.keys(patch).length === 1 && patch.status === 'inactive';
  if (patchIsDeactivation) {
    return patch as Partial<ToolDefinitionInput>;
  }

  if (draft.execution_kind === 'schema_lookup' && saved.execution_kind === 'schema_lookup') {
    const lookupConfigurationChanged = !valuesAreEqual(
      draft.lookup_config,
      savedLookupInput(saved.lookup_config),
    );
    if (forceLookupConfig || lookupConfigurationChanged) {
      patch.lookup_config = draft.lookup_config;
    }
    return patch as Partial<ToolDefinitionInput>;
  }

  if (draft.execution_kind === 'http' && saved.execution_kind === 'http') {
    HTTP_TOOL_FIELDS.forEach(key => {
      if (!valuesAreEqual(draft[key], saved[key])) patch[key] = draft[key];
    });
  }

  return patch as Partial<ToolDefinitionInput>;
}

/** Parses a form control value into the JSON scalar required by its schema field. */
export function parseLookupFilterValue(
  field: LookupSchemaField,
  rawValue: string,
): string | number | boolean {
  if (field.type === 'boolean') return rawValue === 'true';
  if (field.type === 'integer') {
    const parsed = Number.parseInt(rawValue, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (field.type === 'number') {
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return rawValue;
}
